/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {makeStyles} from '@material-ui/core';
import {ColDef, ColumnState} from 'ag-grid-community';
import _ from 'lodash';
import cloneDeep from 'lodash.clonedeep';
import isEmpty from 'lodash.isempty';
import isNil from 'lodash.isnil';
import isNull from 'lodash.isnull';
import isUndefined from 'lodash.isundefined';
import EntityLabel from '@ion/core/src/entities/EntityLabel';
import {isEUProduction} from '@ion/core/src/environment/environment';
import {translate} from '@ion/core/src/locale/locale';
import {error} from '@ion/core/src/logger/logger';
import ApplicationFeatures from '@ion/core/src/security/ApplicationFeatures';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import SmartSearchPaging from '@ion/core/src/services/SmartSearchPaging';
import {
  AccountLink,
  CRUDPermissions,
  CTCEntity,
  Device,
  Devices,
  EntityStatus,
  GenericObject,
  Link
} from '@ion/core/src/types/entities';
import {DownloadTemplate} from '@ion/core/src/types/templates';
import {booleanToString} from '@ion/core/src/utils/BooleanUtils';
import {formatDateTime} from '@ion/core/src/utils/DateUtil';
import {
  addSlashesToQuery,
  getDeviceFilterValue,
  getDeviceGroupNames,
  getDeviceIonTag,
  getEdgeAppVersion,
  getJpodConfig,
  getJpodFirmware,
  getMultipleSearchKeywords,
  getPendingEdgeHosted,
  getPendingFirmware,
  getPendingJpodConfig,
  getPendingJpodFW,
  getPendingScript,
  getRadioFirmware,
  getVbusDatabase,
  getVersionByPlatformId,
  isDeviceSortParamValid,
  PLATFORM_IDS
} from '@ion/core/src/utils/DeviceUtils';
import {numberSortComparator} from '@ion/core/src/utils/SortUtils';
import ChargeLevel from '@ion/dls/src/components/chargelevel/ChargeLevel';
import {LinkCell} from '@ion/dls/src/components/grid/LinkCell';
import IonSpinner from '@ion/dls/src/components/loading/IonSpinner';
import {AssetTreeRefresh} from '@ion/dls/src/editors/AssetEditor';
import {checkEOLDevice, getSpreadVal} from '@ion/dls/src/editors/DeviceEditorUtils';
import {getUniqItems} from '@ion/dls/src/editors/ListEditorUtils';
import {
  defaultNumberFilterParams,
  defaultStatusFilterParams,
  defaultTextFilterParams
} from '@ion/dls/src/grid/ColumnDefinitions';
import {definitionsToState, resolveDefinitions} from '@ion/dls/src/grid/ColumnResolver';
import {filterColumns} from '@ion/dls/src/grid/columns/utils';
import {ExportClient} from '@ion/dls/src/grid/Exporter';
import {
  BulkHeader,
  GridOperation,
  IonGrid,
  IonGridAPI,
  ViewInheritableGroupProps
} from '@ion/dls/src/grid/IonGrid';
import {
  FilterModel,
  SelectAllCheckbox,
  SortModelType
} from '@ion/dls/src/grid/IonGridUtils';
import {GridResult} from '@ion/dls/src/grid/PaginationControls';
import {PAGE_SIZES} from '@ion/dls/src/grid/settings';
import {useColumnChooser} from '@ion/dls/src/grid/useColumnChooser';
import {useColumnState} from '@ion/dls/src/grid/useColumnState';
import {useLoadGridData} from '@ion/dls/src/grid/useLoadGridData';
import {useBulkOperationResults} from '@ion/dls/src/hooks/useBulkOperationResults';
import {useClearFilters} from '@ion/dls/src/hooks/useClearFilters';
import {useCRUDOperationResults} from '@ion/dls/src/hooks/useCRUDOperationResults';
import {useGridDataUpdate} from '@ion/dls/src/hooks/useGridDataUpdate';
import useGlobalProps from '@ion/dls/src/props/GlobalPropsConfig';
import {
  DLSTheme,
  GridPageSizeProps,
  ViewSubGroupsProps
} from '@ion/dls/src/types/types';
import {translateStatus} from '@ion/dls/src/utils/ComponentUtils';
import {
  getStatusFilterParams,
  processExportColumns,
  verifySupplementalCalls
} from '@ion/dls/src/utils/GridUtils';
import {
  createModularSearchFilters,
  getQueryValue,
  hasANDkey,
  hasORkey
} from '@ion/features/src/search/utils';
import {excludeGivenService} from '@ion/query/src/utils/helpers';
import useStopPageMonitor from '@ion/telemetry/src/apm/hooks/useStopPageMonitor';
import {useCurrentGlobalGroupId} from '../../hooks/useCurrentGlobalGroupId';
import {useGroupRefresh} from '../../hooks/useGroupRefresh';
import {useAuthenticatedUser} from '../../pages/admin/users/useAuthenticatedUser';
import {exportDevices} from '../../store/actions/exporter';
import {dispatchGridColumnsChange, sortColumns} from '../../store/helpers';
import {gridDefaultPageSize} from '../../utils/helpers';
import {AccountCell} from '../admin/accounts/AccountCell';
import {GridOperations} from '../admin/AdminContainer';
import {assetEditResultSelector} from '../admin/assets/selector';
import {viewAdminAsset} from '../admin/assets/state';
import {MAX_VIEW_DEVICES} from './BulkViewDevicesDialog';
import DeviceGroupInheritableLinks from './DeviceGroupInheritableLinks';
import {DeviceGroupLinks} from './DeviceGroupLinks';
import {DeviceRowEditor} from './DeviceRowEditor';
import {DEFAULT_SORT_PARAM} from './Devices';
import {DevicesActionMenu} from './DevicesActionMenu';
import {EolCell} from './EolCell';
import {
  getAssociatedDeviceMobileId,
  getICNInfo,
  getLastReportedTime,
  getViewDeviceTabs
} from './helpers';
import JobIdLinks from './JobIdLinks';
import {
  createModularSearchQueryString,
  getExcludeDevicesQuery,
  getNewSearchCriteria,
  hasAnySearch,
  hasMaxSearch
} from './ModularSearchUtils';
import PendingEdgeHostedCell from './PendingEdgeHostedCell';
import PendingJobIdLinks from './PendingJobIdLinks';
import PendingJpodConfigLinks from './PendingJpodConfigLinks';
import {
  devicesGridColumnStateSelector,
  devicesModularSearchSelector
} from './selector';
import ServicesCell from './ServicesCell';
import {
  DEFAULT_SMART_SEARCH_QUERY,
  deleteAdminDevices,
  devicesGridRefreshed,
  editAdminDevices,
  getDevicesBySmartSearch,
  getVin,
  loadDeviceDetail,
  NAME,
  SET_GRID_COLUMNS,
  setDevicesTabs,
  setFilterModel,
  setSelectedDevicesTab,
  setSentinelTab,
  showBulkViewDevices,
  viewAdminDevice,
  viewDevice
} from './state';
import {devicesGridRefreshSelector} from './state-selectors';
import {SearchFields, SentinelTags} from './types';
import useDeviceSmartSearch from './useDeviceSmartSearch';
import {
  formatEnabledDisabledServices,
  getConfigVersion,
  getDeviceIdentifier,
  getFileStatus,
  getFirmwareVersion,
  getMDN,
  getMIN,
  getVbusStatus
} from './ViewDeviceEditorUtils';

export interface DevicesGridProps extends
  ViewSubGroupsProps,
  ViewInheritableGroupProps,
  SelectAllCheckbox,
  GridOperation,
  GridOperations,
  GridPageSizeProps,
  BulkHeader,
  AssetTreeRefresh {
  onSelectAccount?: (value: AccountLink) => void;
  getTotalCount?: (totalCount: number) => void;
  isCtcAdmin?: boolean;
  isIonSuite?: boolean;
  includeDefaultDefinitions?: boolean;
  isHeartBeat?: boolean;
  enableAllHyperLinks?: boolean;
  enabledHyperLinks?: string[];
  excludedColumns?: string[];
  filterableColumns?: string[];
  rowMenusEnabled?: boolean;
  omitHeader?: boolean;
  omitHeaderCTAs?: boolean;
  enableSelectionsExport?: boolean;
  exportLimit?: number;
  isMultiEditEnabled?: boolean;
  includeGlobalSearch?: boolean;
  includeSwapDevice?: boolean;
  searchKeyword?: string;
  selectedDevices: Devices;
  setSelectedDevices?: Function;
  filterModel?: FilterModel;
  rowPerPageTextTransform?: any;
  usePreferedPageSize?: boolean;
  globalGroup?: Link;
  onSCIUpdateClick?: Function;
  areTotalRowsSelected?: boolean;
  searchFilters?: any;
  showBulkHeader?: boolean;
  isSortByParams?: boolean;
  accountsPermissions?: CRUDPermissions;
  refreshInitialization?: Function;
  devicesPermissions?: CRUDPermissions;
  hasVehicleProfile?: boolean;
  hasCreateJob?: boolean;
  selectedItemsCount?: number;
  hasExportLimit?: boolean;
  showEOLDevices?: boolean;
  platformResults?: GenericObject[];
  setSortOn?: Function;
  anySearch?: GenericObject;
  sortOn?: string[];
  hasMultiColumnPreferences?: boolean;
  excludedRows?: CTCEntity[];
  setExcludedRows?: (rows) => void;
  enableExclude?: boolean;
  enableDefaultSort?: boolean;
  reloadOnVisible?: string[];
  withLocation?: boolean;
  withIdReport?: boolean;
  hasInheritableGroups?: boolean;
  Features?: ApplicationFeatures;
  isVbuFwEnabled?: boolean;
  setIsGridLoaded?: Function;
  setSentinelNotFound?: Function;
  hasPendingFileHistory?: boolean;
  setSelectedVehicle?: Function;
  hasVinReadFeature?: boolean;
  onViewInVehicleManager?: (device: Device) => void;
};

// Default excluded columns.
const EXCLUDED_COLUMNS_DEFAULT = [];

// Default filterable columns.
const FILTERABLE_COLUMNS_DEFAULT = [
  'deviceId',
  'deviceName',
  'identifierUI',
  'statusUI',
  'airId',
  'esn',
  'serialNumber'
];

const COLUMNS_LOCALE_PATH = 'admin-devices:grid.columns';

const attributeMappings = {
  deviceId              : 'id',
  deviceName            : 'name',
  identifierUI          : 'esn',
  accountUI             : 'account.title',
  statusUI              : 'status',
  assetName             : 'asset.title',
  idReportTime          : 'extensions.idReportTimestamp',
  androidOSVersion      : 'extensions.androidOSVersion',
  kernelVersion         : 'extensions.kernelVersion',
  deviceType            : 'type.title',
  appIdUI               : 'extensions.appId',
  firmwareVersionUI     : 'extensions.firmwareVersion',
  configVersionUI       : 'extensions.configVersion',
  deviceConfigurationUI : 'deviceConfiguration',
  activationDateUI      : 'activationDate',
  shipDateUI            : 'shipDate',
  lastModifiedOnUI      : 'lastModifiedOn',
  mdnUI                 : 'mdn',
  minUI                 : 'min',
  iccidUI               : 'iccid',
  imeiUI                : 'imei',
  imei2UI               : 'imei2',
  imsiUI                : 'imsi',
  isdnUI                : 'isdn',
  meidUI                : 'meid',
  groupList             : 'groups',
  pendingScriptUI       : 'newVersion',
  pendingFirmwareUI     : 'fileHistoryPending.newVersion',
  vbusStatusUI          : 'extensions.vbusEnabledStatus',
  pendingJpodFWUI       : 'Pending JPOD FW',
  pendingJpodConfigUI   : 'Pending JPOD Config',
  pendingEdgeHostedUI   : 'Pending EdgeApp/ Hosted App',
  jpodFirmwareUI        : 'JPOD Firmware',
  vbusDatabaseUI        : 'VBUS Database',
  jpodConfigUI          : 'JPOD Config',
  androidMdtFirmwareUI  : 'Android MDT Firmware',
  ble3FirmwareUI        : 'BLE3 Firmware',
  vbu3FirmwareUI        : 'VBU3 Firmware',
  radioFirmwareUI       : 'Radio Firmware',
  edgeAppVersionUI      : 'EdgeApp Version',
  mobileIdUI            : 'User Defined Mobile ID',
  mdtUI                 : 'Associated MDT',
  lmuUI                 : 'Associated LMU',
  sentinelUI            : 'Sentinel Association',
  batteryPercentage     : 'extensions.batteryPercentageLeft',
  vbusModeUI            : 'extensions.vbusMode',
  peripherals           : 'features.ATTACHMENTS',
  vehicleProfile        : 'vinInfo',
  iOnTag                : 'deviceConfiguration.title',
  lastReportedTimeUI    : 'lastReportedTime'
};

const useStyles = makeStyles((theme: DLSTheme) => {
  return {
    batteryCell : {
      marginTop : '5px',
      padding   : '0px !important',
      display   : 'flex'
    },
    spinner : {
      display        : 'flex',
      alignItems     : 'center',
      justifyContent : 'center',
      width          : '100%',
      height         : '39px'
    }
  };
});

/**
 * Grid to be rendered on the Admin Devices page.
 */
export const DevicesGrid = ({
  onSelectAccount,
  getAreTotalRowsSelected,
  toggleViewSubGroups,
  getTotalCount,
  onBulkActionsClick,
  selectedDevices,
  setSelectedDevices,
  filterModel,
  globalGroup,
  onSCIUpdateClick,
  setGridPageSize,
  accountsPermissions,
  refreshInitialization,
  devicesPermissions,
  hasVehicleProfile,
  platformResults,
  hasCreateJob,
  sortOn,
  setSortOn,
  anySearch,
  excludedRows,
  setExcludedRows,
  setIsGridLoaded,
  setSentinelNotFound,
  setSelectedVehicle,
  searchKeyword = '',
  gridPageSize = gridDefaultPageSize,
  excludedColumns = EXCLUDED_COLUMNS_DEFAULT,
  filterableColumns = FILTERABLE_COLUMNS_DEFAULT,
  showTreeRefresh = false,
  enableExclude = false,
  rowMenusEnabled = true,
  omitHeader = false,
  omitHeaderCTAs = false,
  enableSelectionsExport = false,
  enableAllHyperLinks = false,
  enabledHyperLinks = [],
  isHeartBeat = false,
  isMultiEditEnabled = false,
  isCtcAdmin = false,
  hasInheritableGroups = false,
  isIonSuite = false,
  includeDefaultDefinitions = false,
  hideSubGroups = false,
  includeSwapDevice = false,
  includeGlobalSearch = false,
  enableSelectAllCheckbox = false,
  usePreferedPageSize = false,
  isVbuFwEnabled = false,
  viewSubGroups = false,
  viewInheritableGroup = false,
  isSortByAlphaNumeric = false,
  areTotalRowsSelected = false,
  showBulkHeader = false,
  isSortByParams = false,
  isPersist = false,
  enableDefaultSort = false,
  hasExportLimit = false,
  showEOLDevices = false,
  reloadOnVisible = [],
  withLocation = false,
  withIdReport = false,
  hasMultiColumnPreferences = false,
  hasPendingFileHistory = false,
  hasVinReadFeature = false,
  onViewInVehicleManager,
  exportLimit = 0,
  selectedItemsCount = 0,
  rowPerPageTextTransform = 'uppercase',
  searchFilters,
  Features
}: DevicesGridProps, ref) => {
  const pageMonitorComplete                   = useStopPageMonitor();
  const dispatch                              = useDispatch();
  const applications                          = useAuthenticatedUser()?.applications;
  const modularSearchParams                   = useSelector(devicesModularSearchSelector);
  const columnState: ColumnState[]            = useSelector(devicesGridColumnStateSelector);
  const editedAssetResult                     = useSelector(assetEditResultSelector);
  const ionGridRef                            = useRef<IonGridAPI>();
  const [refreshRequired, setRefreshRequired] = useState(false);
  const [gridApi, setGridApi]                 = useState(null);
  const [isColumnChanged, setIsColumnChanged] = useState(false);
  const [totalRowsCount, setTotalRowsCount]   = useState<number>();
  const refreshGrid                           = useGroupRefresh();
  const onClearFilters                        = useClearFilters(ionGridRef);
  const isICNUser                             = getICNInfo(isHeartBeat, applications);
  const classes                               = useStyles();
  const maxLimit                              = SmartSearchPaging.MAX_LIMIT;
  const gridExportCount                       = areTotalRowsSelected ? totalRowsCount
    : selectedItemsCount || totalRowsCount;
  const {editResult}                          = useCRUDOperationResults(NAME);
  const accountID                             = useCurrentGlobalGroupId();
  const {loadDevices: loadSentinelDevice}     = useDeviceSmartSearch();
  const {
    hasUpdatePermission,
    hasDeletePermission
  } = devicesPermissions || {};

  const {bulkEditResult}   = useBulkOperationResults(NAME);
  const refreshDeviceGrid  = useSelector(devicesGridRefreshSelector);
  const hasPlatformResults = platformResults !== undefined;
  
  const defaultColumnDefinitions = useMemo(() =>
    getColumnDefinitions({
      excludedColumns,
      filterableColumns,
      enableAllHyperLinks,
      includeGlobalSearch,
      enabledHyperLinks,
      isCtcAdmin,
      isIonSuite,
      isHeartBeat,
      hasVehicleProfile,
      isICNUser,
      showEOLDevices,
      isSortByAlphaNumeric,
      isVbuFwEnabled
    }),
  [
    excludedColumns,
    filterableColumns,
    enableAllHyperLinks,
    includeGlobalSearch,
    enabledHyperLinks,
    isCtcAdmin,
    isIonSuite,
    isHeartBeat,
    hasVehicleProfile,
    isICNUser,
    showEOLDevices,
    isSortByAlphaNumeric,
    isVbuFwEnabled
  ]);

  const {definitions, defaultDefinitions} = useColumnState({
    columnDefinitions : defaultColumnDefinitions,
    columnState
  });

  const {
    showColumnChooser,
    columnDefinitions,
    onColumnChooserSave,
    toggleColumnChooser
  } = useColumnChooser((includeGlobalSearch || includeDefaultDefinitions) ? defaultDefinitions : definitions);

  const usePreferredDateTimeFormater     = useGlobalProps('DevicesGrid.usePreferredDateTimeFormater') as boolean;
  const anySearchForOnlyDisplayedColumns = useGlobalProps('DevicesGrid.anySearchForOnlyDisplayedColumns') as boolean;

  useGridDataUpdate(NAME, ionGridRef.current, devicesGridRefreshed);

  useEffect(() => {
    if (gridApi && editedAssetResult) {
      const {id: assetId, name: assetName} = editedAssetResult;
      gridApi.forEachNode((row: {data: GenericObject}) => {
        const data = row?.data;
        const asset = data?.asset;
        if (asset && entityId(asset.href) === assetId && assetName !== asset.title) {
          ionGridRef?.current?.updateRowNodes?.([{
            ...data,
            asset : {
              ...asset,
              title : assetName
            }
          }]);
        }
      });
    }
  }, [editedAssetResult]);

  const getExportColumnDefinitions = useCallback(() => {
    const ionGridData = ionGridRef?.current?.getExportParams();
    const {columns, sortOn} = ionGridData;
    const customColumnBuilder = {};
    const nameMappingColumns = {
      deviceType           : 'typeName',
      identifierUI         : 'identifier',
      assetName            : 'asset',
      pegBehaviorName      : 'pegBehavior',
      enabledServicesUI    : 'enabledServices',
      disabledServicesUI   : 'disabledServices',
      createdOnUI          : 'createdOn',
      lastAvlCommUI        : 'lastAvlCommunication.locationTime',
      firstIdReportTime    : 'idReport.firstIdReportTime',
      vbusStatusUI         : 'vbusStatus',
      vinUI                : 'vin',
      pendingScriptUI      : 'newVersion',
      pendingFirmwareUI    : 'fileHistoryPending.newVersion',
      eolUI                : 'eol',
      androidOSVersion     : 'extensions.androidOSVersion',
      kernelVersion        : 'extensions.kernelVersion',
      pendingJpodFWUI      : 'pendingJpodFw',
      pendingJpodConfigUI  : 'pendingJpodConfig',
      pendingEdgeHostedUI  : 'pendingEdgeApp',
      jpodFirmwareUI       : 'jpodFirmware',
      vbusDatabaseUI       : 'vbusDatabase',
      jpodConfigUI         : 'jpodConfig',
      androidMdtFirmwareUI : 'androidMdtFirmware',
      macAddressUI         : 'macAddress',
      ble3FirmwareUI       : 'ble3Firmware',
      vbu3FirmwareUI       : 'vbu3Firmware',
      radioFirmwareUI      : 'radioFirmware',
      edgeAppVersionUI     : 'edgeAppVersion',
      mobileIdUI           : 'mobileId',
      mdtUI                : 'mdt',
      lmuUI                : 'lmu',
      sentinelUI           : 'sentinelAssociation',
      vbusModeUI           : 'extensions.vbusMode',
      batteryPercentage    : 'extensions.batteryPercentageLeft',
      peripherals          : 'features.ATTACHMENTS',
      vehicleProfile       : 'vinInfo',
      configStatusUI       : 'configStatus',
      firmwareStatusUI     : 'firmwareStatus',
      lastReportedTimeUI   : 'lastReportedTime'
    };
    columns.forEach(column => {
      if (column.headerName) {
        const fieldLabel = nameMappingColumns[column.field] || attributeMappings[column.field] || column.field;
        customColumnBuilder[fieldLabel] = column.headerName;
      }
    });
    return {columns: customColumnBuilder, sortOn};
  }, []);

  // Provides reference to the grid's api.
  const onGridReady = useCallback((api) => {
    setGridApi(api);
  }, []);

  /**
   * Returns a reference to the grids public api ..
   */
  useImperativeHandle(ref, () => ({
    toggleColumnChooser,
    getExportColumnDefinitions,
    onExport,
    onRefresh,
    unSelectRows,
    onClearFilters,
    columnKeys
  }));

  useEffect(() => {
    const immediateRefresh = Boolean(
      refreshDeviceGrid
    );
    setRefreshRequired(immediateRefresh);
  }, [refreshDeviceGrid]);

  useEffect(() => {
    if (includeGlobalSearch && !isNil(searchKeyword)) {
      setRefreshRequired(!refreshRequired);
    }
  }, [includeGlobalSearch, searchKeyword]);

  useEffect((): void => {
    getTotalCount?.(totalRowsCount);
  }, [totalRowsCount]);

  const onColumnSelectionsUpdate = function(columns: ColDef[]): void {
    setIsColumnChanged(true);
    onColumnChooserSave(columns);
    dispatch(dispatchGridColumnsChange(definitionsToState(columns), NAME, SET_GRID_COLUMNS) as any);
  };

  const onColumnStateUpdate = function(state: ColumnState[]): void {
    if (!includeGlobalSearch && !isEmpty(state) && !includeDefaultDefinitions) {
      dispatch(dispatchGridColumnsChange(state, NAME, SET_GRID_COLUMNS) as any);
    }
  };

  const defaultSort: SortModelType = {
    colId : 'name',
    sort  : 'asc'
  };

  const getFilter = (filter) => {
    if (!isNull(filter)) {
      const filterData = Object.entries(filter);
      let globalSearchStr = `(name: *${searchKeyword}* OR esn: *${searchKeyword}*)`;
      let filterStr = '(id:*)';
      filterData.forEach(item => {
        const filterValue = getDeviceFilterValue(item[1] as string);
        const filterKey = item[0];
        if (includeGlobalSearch) {
          globalSearchStr += ` AND ${filterKey}: ${filterValue}`;
        }
        filterStr += ` AND ${filterKey}: (${filterValue})`;
      });
      return includeGlobalSearch ? globalSearchStr : filterStr;
    }
    if (includeGlobalSearch && (hasORkey(searchKeyword) || hasANDkey(searchKeyword))) {
      searchKeyword = addSlashesToQuery(searchKeyword);
      return getQueryValue(searchKeyword);
    }
    return includeGlobalSearch ? getMultipleSearchKeywords(searchKeyword) : DEFAULT_SMART_SEARCH_QUERY;
  };

  const getDeviceMap = useCallback(() => {
    const deviceMap = {};
    ([(editResult || {}), ...(bulkEditResult as any || [])])?.forEach?.(result => {
      deviceMap[result.id] = result;
    });
    return deviceMap;
  }, [editResult, bulkEditResult]);

  // Intercepts the callback from getting the devices to pull out relevant data.
  const devicesGottenCallbackInterceptor = useCallback((callback: Function):
  (result: {items: Device[], totalCount: number}, options) => void => {
    return (result, options) => {
      setIsGridLoaded(true);
      setTotalRowsCount(result?.totalCount);
      const map = getDeviceMap();
      const {items, totalCount} = result;
      const results = {
        items : items.map((item) => {
          const {id, version} = item;
          const mapped = map[id];
          // If mapped device version is higher than the cached
          // version of smart search result, that should be
          // replaced by the updated version (mapped)
          if (version < mapped?.version) {
            return mapped;
          } else {
            return item;
          }
        }),
        totalCount
      };
      callback?.(results, platformResults);
    };
  }, [getDeviceMap, platformResults]);

  const columnKeys = useMemo(() => {
    if (anySearchForOnlyDisplayedColumns) {
      const map = {deviceType: 'type.title'};
      return (
        defaultDefinitions?.map(({field}) => map[field] || attributeMappings[field] || field).filter(key => (
          !key.includes('batteryPercentageLeft') && !key.includes('lastReportedTime')
        ))
      );
    }
  }, [defaultDefinitions]);

  function getDevices({
    page,
    pageSize,
    sort,
    filter,
    callback
  }) {
    setSortOn(sort);
    const {
      includeHeartBeat,
      includeVin,
      includeWithLocation,
      includeWithIdReport,
      includeInheritableGroups,
      includePendingFileHistory
    } = verifySupplementalCalls({
      columns   : isColumnChanged ? columnDefinitions : definitions,
      isICNUser : isHeartBeat,
      withLocation,
      withIdReport,
      hasVehicleProfile,
      hasInheritableGroups,
      reloadOnVisible,
      hasPendingFileHistory
    });
    dispatch(getDevicesBySmartSearch({
      isHeartBeat           : includeHeartBeat,
      hasVehicleProfile     : includeVin,
      withLocation          : includeWithLocation,
      withIdReport          : includeWithIdReport,
      hasInheritableGroups  : includeInheritableGroups,
      hasPendingFileHistory : includePendingFileHistory,
      viewInheritableGroup  : viewInheritableGroup,
      showEOLDevices        : showEOLDevices,
      globalGroup           : globalGroup,
      filter                : getFilter(filter),
      callback              : devicesGottenCallbackInterceptor(callback),
      includeSubGroups      : viewSubGroups,
      includeAccountFilter  : !includeGlobalSearch,
      includeSortParams     : isSortByParams && isDeviceSortParamValid(sort),
      includeParams         : isSortByParams,
      includeGroups         : true,
      includeDeviceSettings : true,
      usePulse              : isCtcAdmin,
      pageId                : page + 1,
      pageSize,
      sort,
      columnKeys,
      isVbuFwEnabled
    }) as any);
  };
  
  const safeLoadDevices = useCallback((params) => {
    if (hasPlatformResults) {
      getDevices(params);
    }
  }, [hasPlatformResults, getDevices]);

  const loadDevices = useLoadGridData({
    loadData : safeLoadDevices,
    filterModel,
    gridApi
  });

  useEffect(() => {
    if (platformResults?.length > 0 && gridApi) {
      gridApi.refreshCells({
        force : true
      });
    }
  }, [platformResults, gridApi]);

  const onExport = function(downloadParams?: DownloadTemplate) {
    const params: ExportClient = ionGridRef.current?.getExportParams(
      viewSubGroups,
      enableSelectionsExport,
      createModularSearchFilters(modularSearchParams)
    );

    if (enableDefaultSort) {
      params.sortOn = DEFAULT_SORT_PARAM;
    } if (downloadParams?.columns) {
      params.columns = processExportColumns(downloadParams?.columns);
    }

    params.exportLimit          = exportLimit;
    params.gridExportCount      = gridExportCount;
    params.areTotalRowsSelected = areTotalRowsSelected;
    const filters               = searchFilters && !isEmpty(searchFilters) ? searchFilters : params?.filterOn;
    const excludedIds           = excludedRows.map(({id}) => id);
    params.queryString          = createModularSearchQueryString(filters);

    if (hasAnySearch(filters) && hasExportLimit) {
      params.includeSortParams = true;
      params.includeParams     = true;
      params.sortOn            = anySearch;
    } if (hasMaxSearch(filters) && hasExportLimit) {
      params.includeSortParams = true;
      params.includeParams     = true;
      params.maxSearch         = true;
      const maxSearch          = getNewSearchCriteria({
        search   : searchFilters,
        account  : accountID,
        sort     : DEFAULT_SORT_PARAM[0],
        excluded : true,
        viewSubGroups,
        excludedIds,
        isVbuFwEnabled
      });
      params.sortOn            = maxSearch.sortOn;
    } if (excludedRows?.length && hasExportLimit) {
      params.includeSortParams = true;
      params.includeParams     = true;
      params.sortOn            = getExcludeDevicesQuery(
        {
          search    : searchFilters,
          accountId : accountID,
          excludedIds,
          viewSubGroups,
          isVbuFwEnabled
        })?.smartSearch?.search;
    }

    const {
      includeHeartBeat,
      includeVin,
      includeWithLocation,
      includeWithIdReport,
      includeInheritableGroups,
      includePendingFileHistory
    } = verifySupplementalCalls({
      columns   : isColumnChanged ? columnDefinitions : definitions,
      isICNUser : isHeartBeat,
      withLocation,
      withIdReport,
      hasVehicleProfile,
      hasInheritableGroups,
      reloadOnVisible,
      hasPendingFileHistory
    });
    
    dispatch(exportDevices(
      params,
      downloadParams?.columns ? downloadParams : undefined,
      isCtcAdmin,
      hasExportLimit,
      platformResults,
      columnKeys,
      includeVin,
      includeWithLocation,
      includeWithIdReport,
      includeInheritableGroups,
      includePendingFileHistory,
      viewInheritableGroup,
      isVbuFwEnabled,
      includeHeartBeat,
      viewSubGroups
    ) as any);
  };

  const onRefresh = function() {
    setRefreshRequired(true);
    setSelectedDevices([]);
  };

  function onBulkEditDevices(devices: Devices) {
    dispatch(editAdminDevices(devices));
  };

  function onDeleteDevices(devices: Devices) {
    dispatch(deleteAdminDevices(devices));
  };

  function onBulkViewDevices(devices: Devices) {
    let displayDevices = cloneDeep(devices);
    if (devices?.length > MAX_VIEW_DEVICES) {
      displayDevices = devices.slice(0, MAX_VIEW_DEVICES);
    }
    const items = getViewDeviceTabs(displayDevices);
    const selectedTab = displayDevices?.[0];
    dispatch(showBulkViewDevices(devices));
    dispatch(setDevicesTabs(items, selectedTab));
    dispatch(setSelectedDevicesTab(selectedTab));
  };

  const onAssetClick = ({asset}: Device) => {
    dispatch(viewAdminAsset(entityId(asset?.href), true, true) as any);
  };
  
  const onVinUICellClick = (device: Device) => {
    setSelectedVehicle({
      vinId       : getVin(device),
      testProfile : device?.vinInfo?.toLowerCase()
    });
  };

  const unSelectRows = useCallback(() => {
    gridApi?.forEachNode((node) => {
      if (node.selected) {
        node.setSelected(false);
      }
    });
  }, [gridApi]);

  const onSelectionChanged = useCallback((event) => {
    const rowsSelected = event.api.getSelectedRows();
    const uniqItems = getUniqItems(rowsSelected);
    setSelectedDevices(uniqItems);
  }, []);

  const getActionMenu = useMemo(() => (
    <DevicesActionMenu selectedDevices={selectedDevices}
      onSCIUpdateClick={onSCIUpdateClick}
      devicesPermissions={devicesPermissions}
      hasCreateJob={hasCreateJob}
      areTotalRowsSelected={areTotalRowsSelected}/>
  ), [selectedDevices, areTotalRowsSelected]);

  const onFilterChanged = useCallback(function() {
    const filterModel = gridApi?.getFilterModel();
    if (filterModel) {
      dispatch(setFilterModel(filterModel));
    }
  }, [gridApi]);

  const getICNHeartBeatInfo = useCallback((isFirstIcn, heartBeatInfo) => {
    if (!isUndefined(heartBeatInfo)) {
      return isFirstIcn ? formatDateTime(heartBeatInfo?.firstHeartBeatDateTime)
        : formatDateTime(heartBeatInfo?.lastHeartBeatDateTime);
    }
  }, []);

  const getSentinelDevice = useCallback((device: Device) => {
    let mobileId                   = '';
    let mobileIdType               = '';
    const associatedDevices        = device?.extensions?.associatedDevices;
    const associatedDevicesIsArray = Array.isArray(associatedDevices);
    
    if (associatedDevicesIsArray) {
      associatedDevices?.forEach?.(associatedDevice => {
        if (
          (associatedDevice?.tags?.[0] === SentinelTags.PRIMARY) ||
          (associatedDevice?.tags?.[0] === SentinelTags.SECONDARY)
        ) {
          mobileId     = associatedDevice?.mobileId;
          mobileIdType = associatedDevice?.mobileIdType;
        }
      });
    }
    return {mobileId, mobileIdType};
  }, []);

  const onDeviceClick = useCallback((device: Device) => {
    return function() {
      loadDeviceViewDialog({data: device}, true);
    };
  }, []);

  const onViewSentinelDevice = useCallback((device: Device) => {
    const sentinelDevice = getSentinelDevice(device);
    const mobileId       = sentinelDevice?.mobileId;
    const mobileIdType   = sentinelDevice?.mobileIdType;
    return function() {
      getDeviceFromIdentifiers(mobileId, mobileIdType);
    };
  }, []);

  const getDeviceFromIdentifiers = useCallback((searchKeyword: string, searchField: string) => {
    try {
      loadSentinelDevice({
        includeSubGroups : true,
        searchKeyword    : searchKeyword?.trim(),
        accountId        : accountID,
        searchField
      }, {
        onSuccess : (results) => {
          const resultsCount = results?.totalCount;
          if (resultsCount > 0 && resultsCount === 1) {
            const deviceObj = results?.devices?.[0];
            loadDeviceViewDialog({data: deviceObj}, true);
            dispatch(setSentinelTab(true));
          } else {
            setSentinelNotFound({identifier: searchKeyword, error: true});
          }
        },
        onError : () => {
          error('Unable to search device: ', `(${searchField}: ${searchKeyword})`);
          setSentinelNotFound({identifier: searchKeyword, error: true});
        }
      });
    } catch (err) {
      error('Unable to search device: ', err);
      setSentinelNotFound({identifier: searchKeyword, error: true});
    }
  }, [accountID]);

  /**
   * Converts a collection of devices and converts them to a list of items specific
   * for display within the Admin Devices Grid, while retaining all of the items existing
   * attributes.
   */
  const constructDevicesGridRows = useCallback((devices: Device[], deviceAppIds: GenericObject[]): GridResult[] => {
    attributeMappings.deviceType = devices;
    pageMonitorComplete();
    /**
     * This check is for Prevent the CARB Services in EU environment.
     */
    if (isCtcAdmin && isEUProduction()) {
      devices = excludeGivenService('CARB', devices);
    }
    return devices.map(device => ({
      ...device,
      deviceId              : device?.id,
      deviceName            : device?.name,
      deviceType            : device?.type?.title,
      identifierUI          : getDeviceIdentifier(device),
      accountUI             : EntityLabel.get(device?.account),
      assetName             : device?.asset?.title,
      groupList             : getDeviceGroupNames(device),
      enabledServicesUI     : formatEnabledDisabledServices(device, true)?.join?.(', '),
      disabledServicesUI    : formatEnabledDisabledServices(device)?.join?.(', '),
      statusUI              : translateStatus(device?.status),
      iOnTag                : getDeviceIonTag(device),
      appIdUI               : device?.extensions?.appId || null,
      deviceConfigurationUI : device?.deviceConfiguration?.title,
      firmwareVersionUI     : getFirmwareVersion(device, isCtcAdmin),
      configVersionUI       : getConfigVersion(device, true, isCtcAdmin),
      mdnUI                 : getMDN(device),
      minUI                 : getMIN(device),
      iccidUI               : device?.iccid,
      imeiUI                : device?.imei,
      imei2UI               : device?.imei2,
      imsiUI                : device?.imsi,
      isdnUI                : device?.isdn,
      meidUI                : device?.meid,
      configStatusUI        : getFileStatus(device, {config: PLATFORM_IDS.config, peg2: PLATFORM_IDS.peg2}),
      firmwareStatusUI      : getFileStatus(device, {firmware: PLATFORM_IDS.firmware}),
      lastReportedTimeUI    : getLastReportedTime(device, usePreferredDateTimeFormater),
      lastAvlCommUI         : formatDateTime(device?.lastAvlCommunication?.locationTime),
      idReportTime          : formatDateTime(device?.extensions?.idReportTimestamp),
      firstIdReportTime     : formatDateTime(device?.idReport?.firstIdReportTime),
      activationDateUI      : formatDateTime(device?.activationDate),
      carrierStatus         : device?.carrierStatus,
      createdOnUI           : formatDateTime(device?.createdOn),
      icnEnabled            : device?.icnEnabled,
      ipAddress             : device?.ipAddress,
      macAddressUI          : device?.macAddress,
      pegBehaviorName       : device?.pegBehavior?.title,
      lastModifiedOnUI      : formatDateTime(device?.lastModifiedOn),
      shipDateUI            : formatDateTime(device?.shipDate),
      firstIcn              : getICNHeartBeatInfo(true, device?.heartBeatInfo),
      lastIcn               : getICNHeartBeatInfo(false, device?.heartBeatInfo),
      vbusStatusUI          : getVbusStatus(device),
      vinUI                 : getVin(device),
      androidOSVersion      : device?.extensions?.androidOSVersion,
      kernelVersion         : device?.extensions?.kernelVersion,
      pendingScriptUI       : getPendingScript(device),
      pendingFirmwareUI     : getPendingFirmware(device),
      eolUI                 : booleanToString(checkEOLDevice(device, deviceAppIds)),
      pendingJpodFWUI       : getPendingJpodFW(device, !isVbuFwEnabled),
      pendingJpodConfigUI   : getPendingJpodConfig(device, !isVbuFwEnabled),
      pendingEdgeHostedUI   : getPendingEdgeHosted(device),
      jpodFirmwareUI        : getJpodFirmware(device, !isVbuFwEnabled),
      vbusDatabaseUI        : getVbusDatabase(device),
      jpodConfigUI          : getJpodConfig(device, !isVbuFwEnabled),
      androidMdtFirmwareUI  : getVersionByPlatformId(device, PLATFORM_IDS.androidMdtFirmware),
      ble3FirmwareUI        : getVersionByPlatformId(device, PLATFORM_IDS.ble3Firmware),
      vbu3FirmwareUI        : getVersionByPlatformId(device, PLATFORM_IDS.vbu3Firmware),
      radioFirmwareUI       : getRadioFirmware(device),
      edgeAppVersionUI      : getEdgeAppVersion(device),
      mobileIdUI            : device?.userDefinedMobileId,
      mdtUI                 : getAssociatedDeviceMobileId(device, SearchFields.MDT),
      lmuUI                 : getAssociatedDeviceMobileId(device, SearchFields.LMU),
      sentinelUI            : getSentinelDevice(device)?.mobileId,
      sentinelMobileIdType  : getSentinelDevice(device)?.mobileIdType,
      vbusModeUI            : device?.extensions?.vbusMode || null,
      peripherals           : getSpreadVal(device?.features?.ATTACHMENTS),
      batteryPercentage     : device?.extensions?.batteryPercentageLeft,
      vehicleProfile        : device?.vinInfo
    })) as GridResult[];
  }, [usePreferredDateTimeFormater]);

  // invokes to load device view dialog on row double click or any provided grid link -
  // the View Device dialog is only ever mounted for ctc-admin (ion-suite has no
  // DeviceActionDialogs/ViewDeviceDialog instance to show it), so dispatching this
  // for ion-suite left its redux state stuck in a "viewing" state with nothing to
  // close it ...
  const loadDeviceViewDialog = useCallback((e, onDeviceLinkClick = false) => {
    if (isCtcAdmin) {
      const {data: device, event} = e;
      const isLinkCell            = event?.target?.getAttribute('col-id') ?? '';
      if (onDeviceLinkClick || !isEmpty(isLinkCell)) {
        dispatch(viewDevice(device) as any);
        dispatch(loadDeviceDetail(device));
        dispatch(viewAdminDevice(device));
      }
    }
  }, [isCtcAdmin]);

  const columnComponents = {
    rowMenu : item => (
      <DeviceRowEditor showEditAction
        showAuditAction={isCtcAdmin}
        showDeleteAction={isCtcAdmin && !includeGlobalSearch}
        showViewAction={isCtcAdmin}
        showViewInVehicleManagerAction={Boolean((hasVehicleProfile || hasVinReadFeature) && getVin(item?.data))}
        device={item?.data}
        refreshDevice={isCtcAdmin}
        devicesPermissions={devicesPermissions}
        onViewInVehicleManager={onViewInVehicleManager}
        showSwapAction={includeSwapDevice && item?.data?.status === EntityStatus.Enabled} />
    ),
    accountName : (item: GridResult) => (
      <AccountCell item={item}
        isCtcAdmin={isCtcAdmin}
        Features={Features}
        accountsPermissions={accountsPermissions}
        refreshInitialization={refreshInitialization}
        onSelectAccount={onSelectAccount} />
    ),
    assetCell : (item: any) => (
      <LinkCell data={item}
        onClick={function() {
          onAssetClick(item?.data);
        }} />
    ),
    vinUICell : (item: any) => {
      return hasVinReadFeature
        ? <LinkCell data={item}
          onClick={function() {
            onVinUICellClick(item?.data);
          }} />
        : getVin(item?.data);
    },
    deviceCell : item => <LinkCell data={item}
      textTransform={'none'}
      onClick={onDeviceClick(item?.data)} />,
    sentinelDeviceCell : item => <LinkCell data={item}
      textTransform={'none'}
      onClick={onViewSentinelDevice(item?.data)} />,
    batteryCell : item => (
      <div className={classes.batteryCell}>
        <ChargeLevel charge={item?.data?.batteryPercentage} isCardVariant isBatteryCell />
      </div>
    ),
    groupsUI : (item: GridResult) => {
      return hasInheritableGroups
        ? <DeviceGroupInheritableLinks item={item} />
        : <DeviceGroupLinks data={item?.data} />;
    },
    scriptIdCell : (item: GridResult) => {
      const filterPlatformIds = item?.data?.fileHistoryPending?.filter((item) =>
        item.platformId === PLATFORM_IDS.config || item.platformId === PLATFORM_IDS.peg2
      );
      return <JobIdLinks data={filterPlatformIds?.[0]} pendingJobIds={filterPlatformIds} isPendingScript/>;
    },
    firmwareIdCell : (item: GridResult) => {
      const filterPlatformIds = item?.data?.fileHistoryPending?.filter((item) =>
        item.platformId === PLATFORM_IDS.firmware
      );
      return <JobIdLinks data={filterPlatformIds?.[0]} pendingJobIds={filterPlatformIds} isPendingFirmware/>;
    },
    enabledServicesCell : (item: GridResult) => {
      return (
        <ServicesCell columnName='enabledServicesUI'
          features={formatEnabledDisabledServices(item?.data, true)}
          gridApi={item?.api} />
      );
    },
    disabledServicesCell : (item: GridResult) => {
      return (
        <ServicesCell columnName='disabledServicesUI'
          features={formatEnabledDisabledServices(item?.data)}
          gridApi={item?.api} />
      );
    },
    eolCellRenderer       : EolCell,
    pendingJpodFWCell     : PendingJobIdLinks,
    pendingJpodConfigCell : PendingJpodConfigLinks,
    pendingEdgeHostedCell : PendingEdgeHostedCell
  };
  
  return (
    <IonGrid ref={ionGridRef}
      enableExclude={enableExclude}
      showBulkHeader={showBulkHeader}
      isFilterApplied={isCtcAdmin}
      onRowDoubleClicked={loadDeviceViewDialog}
      hasMultiColumnPreferences={hasMultiColumnPreferences}
      isPersist={isPersist}
      excludeRows={excludedRows}
      setExcludedRows={setExcludedRows}
      onBulkActionsClick={onBulkActionsClick}
      rowPerPageTextTransform={rowPerPageTextTransform}
      paginationEnabled
      showCustomPaginationBar
      reloadOnVisible={(
        isHeartBeat || hasVehicleProfile || hasInheritableGroups || withLocation || withIdReport
      ) ? reloadOnVisible : []}
      pageSize={gridPageSize}
      setGridPageSize={setGridPageSize}
      pageConstraintProps={{
        constraint : maxLimit,
        entity     : translate('common:entityLabels.devices')
      }}
      refreshRequired={refreshRequired || (refreshGrid && !includeGlobalSearch)}
      setRefreshRequired={setRefreshRequired}
      loadItems={loadDevices}
      filterModel={filterModel}
      augmentItems={constructDevicesGridRows}
      onFilterChanged={onFilterChanged}
      attributeMappings={attributeMappings}
      checkboxesEnabled={isMultiEditEnabled && !includeGlobalSearch}
      rowMenusEnabled={rowMenusEnabled}
      omitHeader={omitHeader}
      omitHeaderCTAs={omitHeaderCTAs}
      onBulkEdit={onBulkEditDevices}
      onBulkDelete={onDeleteDevices}
      onBulkView={onBulkViewDevices}
      actionMenuItems={isCtcAdmin ? getActionMenu : null}
      disableBulkDelete={!hasDeletePermission || !isCtcAdmin || areTotalRowsSelected}
      disableBulkEdit={areTotalRowsSelected}
      disableBulkActions={!hasUpdatePermission}
      columnDefinitions={columnDefinitions}
      defaultDefinitions={defaultDefinitions}
      showColumnChooser={showColumnChooser}
      onColumnChooserSave={onColumnSelectionsUpdate}
      onColumnsUpdated={onColumnStateUpdate}
      onColumnChooserClose={function() {
        toggleColumnChooser(false);
      }}
      columnComponents={columnComponents}
      pageSizes={PAGE_SIZES}
      defaultSort={defaultSort}
      onGridReady={onGridReady}
      gridOptions={{onSelectionChanged}}
      hideSubGroups={hideSubGroups}
      viewSubGroups={viewSubGroups}
      viewInheritableGroup={viewInheritableGroup}
      getAreTotalRowsSelected={getAreTotalRowsSelected}
      isTotalRowsSelected={areTotalRowsSelected}
      enableSelectAllCheckbox={enableSelectAllCheckbox}
      toggleViewSubGroups={!includeGlobalSearch ? toggleViewSubGroups : undefined}
      usePreferedPageSize={usePreferedPageSize} />
  );
};

/**
 * Validates if a column can be sorted based on if the application is iOn Suite. 
 */
const isSortable = (isIonSuite: boolean): boolean => !isIonSuite;

/**
 * Validates if a column can be hidden based on if the application is iOn Suite. 
 */
const doHideColumn = (isIonSuite: boolean): boolean => !isIonSuite;

/**
 * Gets a column definition with some fields filled with default values appropriate for this component.
 */
const getDefaultColumn = (headerName: string, field: string): ColDef => ({
  headerName,
  field,
  sortable   : false,
  unSortIcon : false,
  filter     : false,
  resizable  : true,
  hide       : true
});

/**
 * Gets a default definition for a column that can be sorted.
 */
const getDefaultSortableColumn = (headerName: string, field: string): ColDef => ({
  ...getDefaultColumn(headerName, field),
  sortable   : true,
  unSortIcon : true
});

const getDefaultVBUSColumn = (headerName: string, field: string, doHideColumn: boolean): ColDef => ({
  ...getDefaultSortableColumn(headerName, field),
  hide : !doHideColumn
});

// Generates the column definition for the ID report time column.
const getIdReportTimeColumnDefinition = (): ColDef => ({
  ...getDefaultSortableColumn(translate(`${COLUMNS_LOCALE_PATH}.idReportTime`), 'idReportTime'),
  headerTooltip : translate('admin-devices:grid.tooltip.idReportTime')
});

const getSentinelAssociationColumnDefinition = (headerName: string, field: string): ColDef => ({
  headerName,
  field,
  cellRenderer       : 'sentinelDeviceCell',
  cellRendererParams : ({data}) => ({data}),
  sortable           : false,
  filter             : 'text',
  resizable          : true,
  unSortIcon         : false,
  hide               : true,
  filterParams       : defaultTextFilterParams
});

/**
 * Returns column definitions for desktop admin devices grid
 */
const getColumnDefinitionsDesktop = ({
  excludedColumns,
  filterableColumns,
  enableAllHyperLinks,
  enabledHyperLinks,
  isCtcAdmin,
  isIonSuite,
  isHeartBeat,
  isICNUser,
  showEOLDevices,
  isSortByAlphaNumeric,
  hasVehicleProfile,
  isVbuFwEnabled
}): ColDef[] => {
  const defaultColumns = translate(COLUMNS_LOCALE_PATH, {returnObjects: true});
  const columns: ColDef[] = [{
    headerName         : translate(`${COLUMNS_LOCALE_PATH}.deviceId`),
    field              : 'deviceId',
    cellRenderer       : 'deviceCell',
    cellRendererParams : ({data}) => ({data}),
    sortable           : true,
    filter             : 'number',
    resizable          : true,
    unSortIcon         : true,
    hide               : !isCtcAdmin,
    filterParams       : defaultNumberFilterParams,
    comparator         : numberSortComparator
  },
  {
    headerName         : translate(`${COLUMNS_LOCALE_PATH}.deviceName`),
    field              : 'deviceName',
    cellRenderer       : 'deviceCell',
    cellRendererParams : ({data}) => ({data}),
    sortable           : true,
    filter             : 'text',
    resizable          : true,
    unSortIcon         : true,
    hide               : false,
    filterParams       : defaultTextFilterParams
  },
  {
    headerName         : translate(`${COLUMNS_LOCALE_PATH}.deviceIdentifier`),
    field              : 'identifierUI',
    cellRenderer       : 'deviceCell',
    cellRendererParams : ({data}) => ({data}),
    sortable           : false,
    filter             : 'text',
    resizable          : true,
    unSortIcon         : false,
    hide               : false,
    filterParams       : defaultTextFilterParams
  },
  {
    headerName : translate(`${COLUMNS_LOCALE_PATH}.deviceType`),
    field      : 'deviceType',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : false
  },
  {
    headerName : translate(`${COLUMNS_LOCALE_PATH}.userEsnMobileId`),
    field      : 'userESN',
    sortable   : true,
    filter     : 'text',
    resizable  : true,
    unSortIcon : true,
    hide       : false
  },
  {
    headerName   : translate(`${COLUMNS_LOCALE_PATH}.eol`),
    field        : 'eolUI',
    sortable     : false,
    filter       : false,
    resizable    : true,
    unSortIcon   : false,
    hide         : !showEOLDevices,
    cellRenderer : 'eolCellRenderer'
  },
  {
    headerName : translate(`${COLUMNS_LOCALE_PATH}.configStatus`),
    field      : 'configStatusUI',
    sortable   : false,
    filter     : false,
    resizable  : true,
    hide       : true,
    unSortIcon : false
  },
  {
    headerName : translate(`${COLUMNS_LOCALE_PATH}.firmwareStatus`),
    field      : 'firmwareStatusUI',
    sortable   : false,
    filter     : false,
    resizable  : true,
    hide       : true,
    unSortIcon : false
  },
  {
    headerName   : translate(`${COLUMNS_LOCALE_PATH}.icnbleivtData`),
    field        : 'enabledServicesUI',
    cellRenderer : 'enabledServicesCell',
    sortable     : false,
    filter       : false,
    resizable    : true,
    unSortIcon   : false,
    hide         : !isCtcAdmin
  },
  {
    headerName   : translate(`${COLUMNS_LOCALE_PATH}.disabledServices`),
    field        : 'disabledServicesUI',
    cellRenderer : 'disabledServicesCell',
    sortable     : false,
    filter       : false,
    resizable    : true,
    unSortIcon   : false,
    hide         : !isCtcAdmin
  },
  {
    headerName         : translate(`${COLUMNS_LOCALE_PATH}.asset`),
    field              : 'assetName',
    cellRenderer       : 'assetCell',
    cellRendererParams : ({data}) => ({data}),
    sortable           : false,
    filter             : false,
    resizable          : true,
    unSortIcon         : false,
    hide               : false
  },
  {
    headerName         : translate(`${COLUMNS_LOCALE_PATH}.account`),
    field              : 'accountUI',
    cellRenderer       : 'accountName',
    cellRendererParams : ({data}) => ({data}),
    sortable           : false,
    filter             : false,
    resizable          : true,
    unSortIcon         : false,
    hide               : false
  },
  {
    headerName : translate(`${COLUMNS_LOCALE_PATH}.iOnTag`),
    field      : 'iOnTag',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : isCtcAdmin
  },
  {
    headerName   : translate(`${COLUMNS_LOCALE_PATH}.status`),
    field        : 'statusUI',
    sortable     : true,
    filter       : 'agSetColumnFilter',
    resizable    : true,
    unSortIcon   : true,
    hide         : false,
    filterParams : getStatusFilterParams()
  },
  {
    headerName   : translate(`${COLUMNS_LOCALE_PATH}.airId`),
    field        : 'airId',
    sortable     : true,
    filter       : 'text',
    resizable    : true,
    unSortIcon   : true,
    hide         : true,
    filterParams : defaultTextFilterParams
  },
  {
    headerName : translate(`${COLUMNS_LOCALE_PATH}.appId`),
    field      : 'appIdUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName   : translate(`${COLUMNS_LOCALE_PATH}.esn`),
    field        : 'esn',
    sortable     : isSortable(isIonSuite),
    filter       : 'text',
    resizable    : true,
    unSortIcon   : true,
    hide         : doHideColumn(isIonSuite),
    filterParams : defaultTextFilterParams
  },
  {
    headerName   : translate(`${COLUMNS_LOCALE_PATH}.serialNumber`),
    field        : 'serialNumber',
    sortable     : isSortable(isIonSuite),
    filter       : 'text',
    resizable    : true,
    unSortIcon   : true,
    hide         : true,
    filterParams : defaultTextFilterParams
  },
  {
    headerName : translate(`${COLUMNS_LOCALE_PATH}.iccid`),
    field      : isCtcAdmin ? 'iccidUI' : 'iccid',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName   : translate(`${COLUMNS_LOCALE_PATH}.imei`),
    field        : isCtcAdmin ? 'imeiUI' : 'imei',
    sortable     : isSortable(isIonSuite),
    filter       : 'text',
    resizable    : true,
    unSortIcon   : true,
    hide         : true,
    filterParams : defaultTextFilterParams
  },
  {
    headerName : translate(`${COLUMNS_LOCALE_PATH}.imei2`),
    field      : 'imei2UI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate(`${COLUMNS_LOCALE_PATH}.imsi`),
    field      : isCtcAdmin ? 'imsiUI' : 'imsi',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate(`${COLUMNS_LOCALE_PATH}.isdn`),
    field      : isCtcAdmin ? 'isdnUI' : 'isdn',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : true
  },
  {
    headerName : translate(`${COLUMNS_LOCALE_PATH}.mdn`),
    field      : 'mdnUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate(`${COLUMNS_LOCALE_PATH}.min`),
    field      : 'minUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate(`${COLUMNS_LOCALE_PATH}.meid`),
    field      : isCtcAdmin ? 'meidUI' : 'meid',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate(`${COLUMNS_LOCALE_PATH}.deviceConfiguration`),
    field      : 'deviceConfigurationUI',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : true
  },
  {
    headerName : translate(`${COLUMNS_LOCALE_PATH}.firmwareVersion`),
    field      : 'firmwareVersionUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate(`${COLUMNS_LOCALE_PATH}.configVersion`),
    field      : 'configVersionUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate(`${COLUMNS_LOCALE_PATH}.category`),
    field      : 'category',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : true
  },
  {
    headerName : translate(`${COLUMNS_LOCALE_PATH}.architecture`),
    field      : 'architecture',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : true
  },
  {
    headerName         : translate(`${COLUMNS_LOCALE_PATH}.groups`),
    field              : 'groupList',
    cellRenderer       : 'groupsUI',
    cellRendererParams : ({data}) => ({data}),
    headerTooltip      : !isIonSuite ? translate('admin-devices:grid.tooltip.groupList') : null,
    sortable           : false,
    filter             : false,
    resizable          : true,
    unSortIcon         : false,
    hide               : true
  },
  {
    headerName    : translate(`${COLUMNS_LOCALE_PATH}.lastEventTime`),
    field         : 'lastReportedTimeUI',
    sortable      : true,
    filter        : false,
    resizable     : true,
    unSortIcon    : true,
    hide          : false,
    headerTooltip : !isIonSuite ? translate('admin-devices:grid.tooltip.lastEventTime') : null
  },
  {
    headerName : translate(`${COLUMNS_LOCALE_PATH}.${isVbuFwEnabled ? 'jpodFirmware' : 'vbuFirmware'}`),
    field      : 'jpodFirmwareUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : !isCtcAdmin
  },
  {
    headerName : translate(`${COLUMNS_LOCALE_PATH}.jpodConfig`),
    field      : 'jpodConfigUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : !isCtcAdmin
  },
  {
    headerName : translate(`${COLUMNS_LOCALE_PATH}.androidMdtFirmware`),
    field      : 'androidMdtFirmwareUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate(`${COLUMNS_LOCALE_PATH}.ble3Firmware`),
    field      : 'ble3FirmwareUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : !isCtcAdmin
  },
  {
    headerName : translate(`${COLUMNS_LOCALE_PATH}.radioFirmware`),
    field      : 'radioFirmwareUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : !isCtcAdmin
  },
  {
    headerName : translate(`${COLUMNS_LOCALE_PATH}.edgeAppVersion`),
    field      : 'edgeAppVersionUI',
    sortable   : false,
    filter     : false,
    resizable  : true,
    hide       : !isCtcAdmin
  },
  {
    headerName : translate(`${COLUMNS_LOCALE_PATH}.mobileId`),
    field      : 'mobileIdUI',
    sortable   : false,
    filter     : false,
    resizable  : true,
    hide       : !isCtcAdmin
  },
  {
    headerName : translate(`${COLUMNS_LOCALE_PATH}.macAddress`),
    field      : 'macAddressUI',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate(`${COLUMNS_LOCALE_PATH}.vbusMode`),
    field      : 'vbusModeUI',
    sortable   : !isVbuFwEnabled,
    filter     : false,
    resizable  : true,
    unSortIcon : !isVbuFwEnabled,
    hide       : !isCtcAdmin
  },
  {
    headerName         : translate(`${COLUMNS_LOCALE_PATH}.pendingFirmware`),
    field              : 'pendingFirmwareUI',
    cellRenderer       : 'firmwareIdCell',
    cellRendererParams : ({data}) => ({data}),
    sortable           : false,
    filter             : false,
    resizable          : true,
    hide               : true
  },
  {
    headerName         : translate(`${COLUMNS_LOCALE_PATH}.pendingScript`),
    field              : 'pendingScriptUI',
    cellRenderer       : 'scriptIdCell',
    cellRendererParams : ({data}) => ({data}),
    sortable           : false,
    filter             : false,
    resizable          : true,
    hide               : true
  },
  {
    headerName         : translate(`${COLUMNS_LOCALE_PATH}.${isVbuFwEnabled ? 'pendingJpodFW' : 'pendingVbuFW'}`),
    field              : 'pendingJpodFWUI',
    cellRenderer       : 'pendingJpodFWCell',
    cellRendererParams : ({data}) => ({data}),
    sortable           : false,
    filter             : false,
    resizable          : true,
    hide               : true
  },
  {
    headerName         : translate(`${COLUMNS_LOCALE_PATH}.pendingJpodConfig`),
    field              : 'pendingJpodConfigUI',
    cellRenderer       : 'pendingJpodConfigCell',
    cellRendererParams : ({data}) => ({data}),
    sortable           : false,
    filter             : false,
    resizable          : true,
    hide               : true
  },
  {
    headerName         : translate(`${COLUMNS_LOCALE_PATH}.pendingEdgeHostedApps`),
    field              : 'pendingEdgeHostedUI',
    cellRenderer       : 'pendingEdgeHostedCell',
    cellRendererParams : ({data}) => ({data}),
    sortable           : false,
    filter             : false,
    resizable          : true,
    hide               : true
  },
  {
    headerName    : translate(`${COLUMNS_LOCALE_PATH}.lastAvlTime`),
    field         : 'lastAvlCommUI',
    sortable      : false,
    filter        : false,
    resizable     : true,
    unSortIcon    : false,
    hide          : true,
    headerTooltip : translate('admin-devices:grid.tooltip.lastAvlTime')
  },
  {
    headerName   : translate(`${COLUMNS_LOCALE_PATH}.androidOSVersion`),
    field        : 'androidOSVersion',
    sortable     : false,
    filter       : 'text',
    resizable    : true,
    unSortIcon   : false,
    hide         : true,
    filterParams : defaultTextFilterParams
  },
  {
    headerName   : translate(`${COLUMNS_LOCALE_PATH}.kernelVersion`),
    field        : 'kernelVersion',
    sortable     : false,
    filter       : 'text',
    resizable    : true,
    unSortIcon   : false,
    hide         : true,
    filterParams : defaultTextFilterParams
  },
  {
    headerName   : translate(`${COLUMNS_LOCALE_PATH}.mdt`),
    field        : 'mdtUI',
    sortable     : false,
    filter       : 'text',
    resizable    : true,
    unSortIcon   : false,
    hide         : true,
    filterParams : defaultTextFilterParams
  },
  {
    headerName   : translate(`${COLUMNS_LOCALE_PATH}.lmu`),
    field        : 'lmuUI',
    sortable     : false,
    filter       : 'text',
    resizable    : true,
    unSortIcon   : false,
    hide         : true,
    filterParams : defaultTextFilterParams
  },
  {
    headerName : translate(`${COLUMNS_LOCALE_PATH}.peripherals`),
    field      : 'peripherals',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : true
  },
  {
    headerName         : translate(`${COLUMNS_LOCALE_PATH}.batteryPercentage`),
    field              : 'batteryPercentage',
    cellRenderer       : 'batteryCell',
    cellRendererParams : ({data}) => ({data}),
    sortable           : true,
    filter             : false,
    resizable          : true,
    unSortIcon         : true,
    hide               : true
  }];
  if (isCtcAdmin) {
    columns.push({
      headerName         : translate(`${COLUMNS_LOCALE_PATH}.vin`),
      field              : 'vinUI',
      cellRenderer       : 'vinUICell',
      cellRendererParams : ({data}) => ({data}),
      sortable           : false,
      filter             : false,
      resizable          : true,
      unSortIcon         : false,
      hide               : true
    });
    columns.push(
      getIdReportTimeColumnDefinition(),
      getDefaultSortableColumn(defaultColumns.vbusStatus, 'vbusStatusUI')
    );
    columns.push(getDefaultColumn(defaultColumns.firstIdReportTime, 'firstIdReportTime'));
    columns.push(
      getDefaultVBUSColumn(defaultColumns.vbusDatabase, 'vbusDatabaseUI', false)
    );
  }
  if (isHeartBeat) {
    columns.push(
      getDefaultSortableColumn(defaultColumns.activationDate, 'activationDateUI'),
      getDefaultColumn(defaultColumns.carrierStatus, 'carrierStatus'),
      getDefaultColumn(defaultColumns.createdOn, 'createdOnUI'),
      getDefaultColumn(defaultColumns.icnEnabled, 'icnEnabled'),
      getDefaultSortableColumn(defaultColumns.ipAddress, 'ipAddress'),
      getDefaultColumn(defaultColumns.pegBehavior, 'pegBehaviorName'),
      getDefaultSortableColumn(defaultColumns.lastModifiedOn, 'lastModifiedOnUI'),
      getDefaultSortableColumn(defaultColumns.shipDate, 'shipDateUI')
    );
  }
  if (hasVehicleProfile) {
    columns.push(getDefaultColumn(defaultColumns.vehicleProfile, 'vehicleProfile'));
  }
  if (isICNUser) {
    columns.push(
      getDefaultColumn(defaultColumns.firstIcn, 'firstIcn'),
      getDefaultColumn(defaultColumns.lastIcn, 'lastIcn')
    );
  }
  if (isVbuFwEnabled) {
    columns.push(
      getDefaultVBUSColumn(defaultColumns.vbu3Firmware, 'vbu3FirmwareUI', isCtcAdmin)
    );
  }
  columns.push(
    getSentinelAssociationColumnDefinition(defaultColumns.sentinelAssociation, 'sentinelUI')
  );
  
  const doRemoveCellRendererCallback = (column: ColDef): boolean => {
    return !(enableAllHyperLinks || enabledHyperLinks?.includes(column?.field));
  };

  return filterColumns({
    columns,
    doRemoveCellRendererCallback,
    excludedColumns,
    filterableColumns,
    isSortByAlphaNumeric
  });
};

/**
 * Returns column definitions for mobile admin devices grid
 */
const getColumnDefinitionsMobile = ({
  excludedColumns,
  enableAllHyperLinks,
  enabledHyperLinks,
  filterableColumns,
  isCtcAdmin,
  isIonSuite,
  isHeartBeat,
  hasVehicleProfile,
  isICNUser,
  showEOLDevices,
  isSortByAlphaNumeric,
  isVbuFwEnabled
}): ColDef[] => {
  const defaultColumns = translate(COLUMNS_LOCALE_PATH, {returnObjects: true});
  const columns: ColDef[] = [{
    headerName         : translate(`${COLUMNS_LOCALE_PATH}.deviceId`),
    field              : 'deviceId',
    cellRenderer       : 'deviceCell',
    cellRendererParams : ({data}) => ({data}),
    sortable           : true,
    filter             : 'number',
    resizable          : true,
    unSortIcon         : true,
    hide               : !isCtcAdmin,
    filterParams       : defaultNumberFilterParams,
    comparator         : numberSortComparator
  },
  {
    headerName         : translate(`${COLUMNS_LOCALE_PATH}.deviceName`),
    field              : 'deviceName',
    cellRenderer       : 'deviceCell',
    cellRendererParams : ({data}) => ({data}),
    sortable           : true,
    filter             : 'text',
    resizable          : true,
    unSortIcon         : true,
    hide               : false,
    filterParams       : defaultTextFilterParams
  },
  {
    headerName         : translate(`${COLUMNS_LOCALE_PATH}.deviceIdentifier`),
    field              : 'identifierUI',
    cellRenderer       : 'deviceCell',
    cellRendererParams : ({data}) => ({data}),
    sortable           : false,
    filter             : 'text',
    resizable          : true,
    unSortIcon         : false,
    hide               : false,
    filterParams       : defaultTextFilterParams
  },
  {
    headerName : translate(`${COLUMNS_LOCALE_PATH}.deviceType`),
    field      : 'deviceType',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : false
  },
  {
    headerName : translate(`${COLUMNS_LOCALE_PATH}.userEsnMobileId`),
    field      : 'userESN',
    sortable   : true,
    filter     : 'text',
    resizable  : true,
    unSortIcon : true,
    hide       : false
  },
  {
    headerName : translate(`${COLUMNS_LOCALE_PATH}.configStatus`),
    field      : 'configStatusUI',
    sortable   : false,
    filter     : false,
    resizable  : true,
    hide       : true,
    unSortIcon : false
  },
  {
    headerName : translate(`${COLUMNS_LOCALE_PATH}.firmwareStatus`),
    field      : 'firmwareStatusUI',
    sortable   : false,
    filter     : false,
    resizable  : true,
    hide       : true,
    unSortIcon : false
  },
  {
    headerName   : translate(`${COLUMNS_LOCALE_PATH}.eol`),
    field        : 'eolUI',
    sortable     : false,
    filter       : false,
    resizable    : true,
    unSortIcon   : false,
    hide         : !showEOLDevices,
    cellRenderer : 'eolCellRenderer'
  },
  {
    headerName   : translate(`${COLUMNS_LOCALE_PATH}.icnbleivtData`),
    field        : 'enabledServicesUI',
    cellRenderer : 'enabledServicesCell',
    sortable     : false,
    filter       : false,
    resizable    : true,
    unSortIcon   : false,
    hide         : !isCtcAdmin
  },
  {
    headerName   : translate(`${COLUMNS_LOCALE_PATH}.disabledServices`),
    field        : 'disabledServicesUI',
    cellRenderer : 'disabledServicesCell',
    sortable     : false,
    filter       : false,
    resizable    : true,
    unSortIcon   : false,
    hide         : !isCtcAdmin
  },
  {
    headerName         : translate(`${COLUMNS_LOCALE_PATH}.asset`),
    field              : 'assetName',
    cellRenderer       : 'assetCell',
    cellRendererParams : ({data}) => ({data}),
    sortable           : false,
    filter             : false,
    resizable          : true,
    unSortIcon         : false,
    hide               : false
  },
  {
    headerName         : translate(`${COLUMNS_LOCALE_PATH}.account`),
    field              : 'accountUI',
    cellRenderer       : 'accountName',
    cellRendererParams : ({data}) => ({data}),
    sortable           : false,
    filter             : false,
    resizable          : true,
    unSortIcon         : false,
    hide               : false
  },
  {
    headerName : translate(`${COLUMNS_LOCALE_PATH}.iOnTag`),
    field      : 'iOnTag',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : isCtcAdmin
  },
  {
    headerName   : translate(`${COLUMNS_LOCALE_PATH}.status`),
    field        : 'statusUI',
    sortable     : true,
    filter       : 'agSetColumnFilter',
    resizable    : true,
    unSortIcon   : true,
    hide         : false,
    filterParams : getStatusFilterParams()
  },
  {
    headerName : translate(`${COLUMNS_LOCALE_PATH}.${isVbuFwEnabled ? 'jpodFirmware' : 'vbuFirmware'}`),
    field      : 'jpodFirmwareUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : !isCtcAdmin
  },
  {
    headerName : translate(`${COLUMNS_LOCALE_PATH}.jpodConfig`),
    field      : 'jpodConfigUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : !isCtcAdmin
  },
  {
    headerName : translate(`${COLUMNS_LOCALE_PATH}.androidMdtFirmware`),
    field      : 'androidMdtFirmwareUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate(`${COLUMNS_LOCALE_PATH}.ble3Firmware`),
    field      : 'ble3FirmwareUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : !isCtcAdmin
  },
  {
    headerName : translate(`${COLUMNS_LOCALE_PATH}.radioFirmware`),
    field      : 'radioFirmwareUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : !isCtcAdmin
  },
  {
    headerName : translate(`${COLUMNS_LOCALE_PATH}.edgeAppVersion`),
    field      : 'edgeAppVersionUI',
    sortable   : false,
    filter     : false,
    resizable  : true,
    hide       : !isCtcAdmin
  },
  {
    headerName : translate(`${COLUMNS_LOCALE_PATH}.mobileId`),
    field      : 'mobileIdUI',
    sortable   : false,
    filter     : false,
    resizable  : true,
    hide       : !isCtcAdmin
  },
  {
    headerName : translate(`${COLUMNS_LOCALE_PATH}.vbusMode`),
    field      : 'vbusModeUI',
    sortable   : !isVbuFwEnabled,
    filter     : false,
    resizable  : true,
    unSortIcon : !isVbuFwEnabled,
    hide       : !isCtcAdmin
  },
  {
    headerName         : translate(`${COLUMNS_LOCALE_PATH}.pendingFirmware`),
    field              : 'pendingFirmwareUI',
    cellRenderer       : 'firmwareIdCell',
    cellRendererParams : ({data}) => ({data}),
    sortable           : false,
    filter             : false,
    resizable          : true,
    hide               : true
  },
  {
    headerName         : translate(`${COLUMNS_LOCALE_PATH}.pendingScript`),
    field              : 'pendingScriptUI',
    cellRenderer       : 'scriptIdCell',
    cellRendererParams : ({data}) => ({data}),
    sortable           : false,
    filter             : false,
    resizable          : true,
    hide               : true
  },
  {
    headerName         : translate(`${COLUMNS_LOCALE_PATH}.${isVbuFwEnabled ? 'pendingJpodFW' : 'pendingVbuFW'}`),
    field              : 'pendingJpodFWUI',
    cellRenderer       : 'pendingJpodFWCell',
    cellRendererParams : ({data}) => ({data}),
    sortable           : false,
    filter             : false,
    resizable          : true,
    hide               : true
  },
  {
    headerName         : translate(`${COLUMNS_LOCALE_PATH}.pendingJpodConfig`),
    field              : 'pendingJpodConfigUI',
    cellRenderer       : 'pendingJpodConfigCell',
    cellRendererParams : ({data}) => ({data}),
    sortable           : false,
    filter             : false,
    resizable          : true,
    hide               : true
  },
  {
    headerName         : translate(`${COLUMNS_LOCALE_PATH}.pendingEdgeHostedApps`),
    field              : 'pendingEdgeHostedUI',
    cellRenderer       : 'pendingEdgeHostedCell',
    cellRendererParams : ({data}) => ({data}),
    sortable           : false,
    filter             : false,
    resizable          : true,
    hide               : true
  },
  {
    headerName   : translate(`${COLUMNS_LOCALE_PATH}.airId`),
    field        : 'airId',
    sortable     : true,
    filter       : 'text',
    resizable    : true,
    unSortIcon   : true,
    hide         : true,
    filterParams : defaultTextFilterParams
  },
  {
    headerName : translate(`${COLUMNS_LOCALE_PATH}.appId`),
    field      : 'appIdUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName   : translate(`${COLUMNS_LOCALE_PATH}.esn`),
    field        : 'esn',
    sortable     : isSortable(isIonSuite),
    filter       : 'text',
    resizable    : true,
    unSortIcon   : true,
    hide         : doHideColumn(isIonSuite),
    filterParams : defaultTextFilterParams
  },
  {
    headerName   : translate(`${COLUMNS_LOCALE_PATH}.serialNumber`),
    field        : 'serialNumber',
    sortable     : isSortable(isIonSuite),
    filter       : 'text',
    resizable    : true,
    unSortIcon   : true,
    hide         : true,
    filterParams : defaultTextFilterParams
  },
  {
    headerName : translate(`${COLUMNS_LOCALE_PATH}.iccid`),
    field      : isCtcAdmin ? 'iccidUI' : 'iccid',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName   : translate(`${COLUMNS_LOCALE_PATH}.imei`),
    field        : isCtcAdmin ? 'imeiUI' : 'imei',
    sortable     : isSortable(isIonSuite),
    filter       : 'text',
    resizable    : true,
    unSortIcon   : true,
    hide         : true,
    filterParams : defaultTextFilterParams
  },
  {
    headerName : translate(`${COLUMNS_LOCALE_PATH}.macAddress`),
    field      : 'macAddressUI',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate(`${COLUMNS_LOCALE_PATH}.imei2`),
    field      : 'imei2UI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate(`${COLUMNS_LOCALE_PATH}.imsi`),
    field      : isCtcAdmin ? 'imsiUI' : 'imsi',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate(`${COLUMNS_LOCALE_PATH}.isdn`),
    field      : isCtcAdmin ? 'isdnUI' : 'isdn',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : true
  },
  {
    headerName : translate(`${COLUMNS_LOCALE_PATH}.mdn`),
    field      : 'mdnUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate(`${COLUMNS_LOCALE_PATH}.min`),
    field      : 'minUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate(`${COLUMNS_LOCALE_PATH}.meid`),
    field      : isCtcAdmin ? 'meidUI' : 'meid',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate(`${COLUMNS_LOCALE_PATH}.deviceConfiguration`),
    field      : 'deviceConfigurationUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate(`${COLUMNS_LOCALE_PATH}.firmwareVersion`),
    field      : 'firmwareVersionUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate(`${COLUMNS_LOCALE_PATH}.configVersion`),
    field      : 'configVersionUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate(`${COLUMNS_LOCALE_PATH}.category`),
    field      : 'category',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate(`${COLUMNS_LOCALE_PATH}.architecture`),
    field      : 'architecture',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName         : translate(`${COLUMNS_LOCALE_PATH}.groups`),
    field              : 'groupList',
    cellRenderer       : 'groupsUI',
    cellRendererParams : ({data}) => ({data}),
    headerTooltip      : !isIonSuite ? translate('admin-devices:grid.tooltip.groupList') : null,
    sortable           : true,
    filter             : false,
    resizable          : true,
    unSortIcon         : true,
    hide               : true
  },
  {
    headerName    : translate(`${COLUMNS_LOCALE_PATH}.lastEventTime`),
    field         : 'lastReportedTimeUI',
    sortable      : true,
    filter        : false,
    resizable     : true,
    unSortIcon    : true,
    hide          : true,
    headerTooltip : !isIonSuite ? translate('admin-devices:grid.tooltip.lastEventTime') : null
  },
  {
    headerName    : translate(`${COLUMNS_LOCALE_PATH}.lastAvlTime`),
    field         : 'lastAvlCommUI',
    sortable      : false,
    filter        : false,
    resizable     : true,
    unSortIcon    : false,
    hide          : true,
    headerTooltip : translate('admin-devices:grid.tooltip.lastAvlTime')
  },
  {
    headerName   : translate(`${COLUMNS_LOCALE_PATH}.androidOSVersion`),
    field        : 'androidOSVersion',
    sortable     : false,
    filter       : 'text',
    resizable    : true,
    unSortIcon   : false,
    hide         : true,
    filterParams : defaultTextFilterParams
  },
  {
    headerName   : translate(`${COLUMNS_LOCALE_PATH}.kernelVersion`),
    field        : 'kernelVersion',
    sortable     : false,
    filter       : 'text',
    resizable    : true,
    unSortIcon   : false,
    hide         : true,
    filterParams : defaultTextFilterParams
  },
  {
    headerName   : translate(`${COLUMNS_LOCALE_PATH}.mdt`),
    field        : 'mdtUI',
    sortable     : false,
    filter       : 'text',
    resizable    : true,
    unSortIcon   : false,
    hide         : true,
    filterParams : defaultTextFilterParams
  },
  {
    headerName   : translate(`${COLUMNS_LOCALE_PATH}.lmu`),
    field        : 'lmuUI',
    sortable     : false,
    filter       : 'text',
    resizable    : true,
    unSortIcon   : false,
    hide         : true,
    filterParams : defaultTextFilterParams
  },
  {
    headerName : translate(`${COLUMNS_LOCALE_PATH}.peripherals`),
    field      : 'peripherals',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : true
  },
  {
    headerName         : translate(`${COLUMNS_LOCALE_PATH}.batteryPercentage`),
    field              : 'batteryPercentage',
    cellRenderer       : 'batteryCell',
    cellRendererParams : ({data}) => ({data}),
    sortable           : true,
    filter             : false,
    resizable          : true,
    unSortIcon         : true,
    hide               : true
  }];
  if (isCtcAdmin) {
    columns.push({
      headerName         : translate(`${COLUMNS_LOCALE_PATH}.vin`),
      field              : 'vinUI',
      cellRenderer       : 'vinUICell',
      cellRendererParams : ({data}) => ({data}),
      sortable           : false,
      filter             : false,
      resizable          : true,
      unSortIcon         : false,
      hide               : true
    });
    columns.push(
      getIdReportTimeColumnDefinition(),
      getDefaultSortableColumn(defaultColumns.vbusStatus, 'vbusStatusUI')
    );
    columns.push(getDefaultColumn(defaultColumns.firstIdReport, 'firstIdReport'));
    columns.push(
      getDefaultVBUSColumn(defaultColumns.vbusDatabase, 'vbusDatabaseUI', false)
    );
  }
  if (isHeartBeat) {
    columns.push(
      getDefaultSortableColumn(defaultColumns.activationDate, 'activationDateUI'),
      getDefaultColumn(defaultColumns.carrierStatus, 'carrierStatus'),
      getDefaultColumn(defaultColumns.createdOn, 'createdOnUI'),
      getDefaultColumn(defaultColumns.icnEnabled, 'icnEnabled'),
      getDefaultSortableColumn(defaultColumns.ipAddress, 'ipAddress'),
      getDefaultColumn(defaultColumns.pegBehavior, 'pegBehaviorName'),
      getDefaultSortableColumn(defaultColumns.lastModifiedOn, 'lastModifiedOnUI'),
      getDefaultSortableColumn(defaultColumns.shipDate, 'shipDateUI')
    );
  }
  if (hasVehicleProfile) {
    columns.push(getDefaultColumn(defaultColumns.vehicleProfile, 'vehicleProfile'));
  }
  if (isICNUser) {
    columns.push(
      getDefaultColumn(defaultColumns.firstIcn, 'firstIcn'),
      getDefaultColumn(defaultColumns.lastIcn, 'lastIcn')
    );
  }
  if (isVbuFwEnabled) {
    columns.push(
      getDefaultVBUSColumn(defaultColumns.vbu3Firmware, 'vbu3FirmwareUI', isCtcAdmin)
    );
  }
  columns.push(
    getSentinelAssociationColumnDefinition(defaultColumns.sentinelAssociation, 'sentinelUI')
  );
  
  const doRemoveCellRendererCallback = (column: ColDef): boolean => {
    return !(enableAllHyperLinks || enabledHyperLinks?.includes(column?.field));
  };
  
  return filterColumns({
    columns,
    doRemoveCellRendererCallback,
    excludedColumns,
    filterableColumns,
    isSortByAlphaNumeric
  });
};

/**
 * Returns column definitions for the admin devices grid in global search desktop mode.
 */
const getColumnDefinitionsDesktopSearch = (): ColDef[] => {
  return [{
    headerName         : translate(`${COLUMNS_LOCALE_PATH}.deviceId`),
    field              : 'deviceId',
    cellRenderer       : 'deviceCell',
    cellRendererParams : ({data}) => ({data}),
    sortable           : true,
    filter             : false,
    resizable          : true,
    unSortIcon         : true,
    hide               : false,
    filterParams       : defaultNumberFilterParams,
    comparator         : numberSortComparator
  },
  {
    headerName         : translate(`${COLUMNS_LOCALE_PATH}.deviceName`),
    field              : 'name',
    cellRenderer       : 'deviceCell',
    cellRendererParams : ({data}) => ({data}),
    sortable           : true,
    filter             : false,
    resizable          : true,
    unSortIcon         : true,
    hide               : false,
    filterParams       : defaultTextFilterParams
  },
  {
    headerName         : translate(`${COLUMNS_LOCALE_PATH}.deviceIdentifier`),
    field              : 'identifierUI',
    cellRenderer       : 'deviceCell',
    cellRendererParams : ({data}) => ({data}),
    sortable           : false,
    filter             : false,
    resizable          : true,
    unSortIcon         : false,
    hide               : false,
    filterParams       : defaultTextFilterParams
  },
  {
    headerName : translate(`${COLUMNS_LOCALE_PATH}.deviceType`),
    field      : 'deviceType',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : false
  },
  {
    headerName   : translate(`${COLUMNS_LOCALE_PATH}.airId`),
    field        : 'airId',
    sortable     : true,
    filter       : false,
    resizable    : true,
    unSortIcon   : true,
    hide         : false,
    filterParams : defaultTextFilterParams
  },
  {
    headerName         : translate(`${COLUMNS_LOCALE_PATH}.asset`),
    field              : 'assetName',
    cellRenderer       : 'assetCell',
    cellRendererParams : ({data}) => ({data}),
    sortable           : false,
    filter             : false,
    resizable          : true,
    unSortIcon         : false,
    hide               : false
  },
  {
    headerName   : translate(`${COLUMNS_LOCALE_PATH}.status`),
    field        : 'statusUI',
    sortable     : true,
    filter       : false,
    resizable    : true,
    unSortIcon   : true,
    hide         : false,
    filterParams : defaultStatusFilterParams
  },
  {
    headerName         : translate(`${COLUMNS_LOCALE_PATH}.account`),
    field              : 'accountUI',
    cellRenderer       : 'accountName',
    cellRendererParams : ({data}) => ({data}),
    sortable           : false,
    filter             : false,
    resizable          : true,
    unSortIcon         : false,
    hide               : false
  }];
};

const getColumnDefinitions = ({
  excludedColumns,
  filterableColumns,
  enableAllHyperLinks,
  enabledHyperLinks,
  includeGlobalSearch,
  isCtcAdmin,
  isIonSuite,
  isHeartBeat,
  hasVehicleProfile,
  isICNUser,
  showEOLDevices,
  isSortByAlphaNumeric,
  isVbuFwEnabled
}) => ({
  desktop : resolveDefinitions(includeGlobalSearch
    ? getColumnDefinitionsDesktopSearch()
    : getColumnDefinitionsDesktop({
      excludedColumns,
      filterableColumns,
      enableAllHyperLinks,
      enabledHyperLinks,
      isCtcAdmin,
      isIonSuite,
      isHeartBeat,
      hasVehicleProfile,
      isICNUser,
      showEOLDevices,
      isSortByAlphaNumeric,
      isVbuFwEnabled
    })),
  mobile : resolveDefinitions(includeGlobalSearch
    ? getColumnDefinitionsDesktopSearch()
    : getColumnDefinitionsMobile({
      excludedColumns,
      filterableColumns,
      enableAllHyperLinks,
      enabledHyperLinks,
      isCtcAdmin,
      isIonSuite,
      isHeartBeat,
      hasVehicleProfile,
      isICNUser,
      showEOLDevices,
      isSortByAlphaNumeric,
      isVbuFwEnabled
    }))
});

export default forwardRef(DevicesGrid);
